import {
    ApiFragment,
    BatchStatus,
    ExportLocation,
    FormSelectOption,
    Group,
    MultiFormSelectOptions,
    Organisation,
    Role,
    User,
    Zone
} from '@stylehaven/components';

import { SharedLanguageService } from '@language';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
export function classificationsToFormSelectOptions(
    classifications: ApiFragment[],
    isLiveData: boolean = true
): MultiFormSelectOptions {
    return {
        options: classifications.map((classification: ApiFragment) => ({
            label: classification.getTitle(),
            value: classification.getMediaObjectId()
        })),
        isLiveData
    } as MultiFormSelectOptions;
}

export type NameId = Zone | Group | ExportLocation;

export function nameIdFormSelectOptions(pluralData: NameId[]): MultiFormSelectOptions {
    return {
        options: pluralData.map((data: NameId) => ({
            label: data.Name,
            value: data.Id
        })),
        isLiveData: false
    } as MultiFormSelectOptions;
}
export function batchStatusValuesToFormSelectOptions(
    batchStatusValues: BatchStatus[],
    translateService: SharedLanguageService,
    includeEmpty: boolean = false,
    emptyOptionLabel: string = ''
): MultiFormSelectOptions {
    let options: FormSelectOption<string | undefined>[] = [];
    if (includeEmpty) {
        if (!emptyOptionLabel) {
            throw new Error(
                'Error converting BatchStatus to FormOptions: include empty requested, but no label provided'
            );
        }

        options.push({ label: emptyOptionLabel, value: undefined });
    }
    options = options.concat(
        batchStatusValues.map(data => ({
            label: translateService.translate(`batches.status.${data}`),
            value: data
        }))
    );
    return {
        options,
        isLiveData: false
    } as MultiFormSelectOptions;
}

export function exportLocationToMultiFormSelectOptions(exportLocations: ExportLocation[]): MultiFormSelectOptions {
    return {
        options: exportLocations.map(exportLocation => ({
            label: exportLocation.Name,
            value: exportLocation.Id,
            disabled: exportLocation.AllowedForAllUsers
        })),
        isLiveData: false
    } as MultiFormSelectOptions;
}

export function usersToFormSelectOptions(
    users: User[],
    disableIfNonEditable: boolean = false,
    disabledInfoMsg?: string
): FormSelectOption[] {
    return users.map((user: User) => {
        let labelExtension = '';
        if (user.FirstName && user.LastName) {
            labelExtension = `(${user.FirstName} ${user.LastName})`;
        }

        const option: FormSelectOption = {
            label: `${user.Login} ${labelExtension}`,
            value: user.Id,
            disabled: disableIfNonEditable && !user.Context.IsEditable,
            disabledInfoMsg: disabledInfoMsg
        };

        return option;
    });
}

export function usersToMultiFormSelectOptions(
    users: User[],
    live: boolean = true,
    disableIfNonEditable: boolean = false,
    disabledInfoMsg: string,
    includeEmpty: boolean = false,
    emptyOptionLabel: string = ''
): MultiFormSelectOptions {
    let options: FormSelectOption<string | undefined>[] = [];
    if (includeEmpty) {
        if (!emptyOptionLabel) {
            throw new Error('Error converting users to FormOptions: include empty requested, but no label provided');
        }

        options.push({ label: emptyOptionLabel, value: undefined });
    }
    options = options.concat(usersToFormSelectOptions(users, disableIfNonEditable, disabledInfoMsg));

    return {
        options,
        isLiveData: live
    } as MultiFormSelectOptions;
}

export function organisationsToFormSelectOptions(organisations: Organisation[]): MultiFormSelectOptions {
    return {
        options: organisations.map((organisation: Organisation) => ({
            label: organisation.LongName,
            value: organisation.Id.toString()
        })),
        isLiveData: false
    } as MultiFormSelectOptions;
}

export function rolesToFormSelectOptions(roles: Role[]): MultiFormSelectOptions {
    return {
        options: roles.map((role: Role) => ({
            label: role.Label,
            value: role.RoleId
        })),
        isLiveData: false
    };
}

export function fragmentsToFormSelectOptions(fragments: ApiFragment[]): MultiFormSelectOptions<ApiFragment> {
    return {
        options: fragments.map((fragment: ApiFragment) => ({
            label: fragment.getTitle(),
            value: fragment
        })),
        isLiveData: true
    } as MultiFormSelectOptions<ApiFragment>;
}

export function combineDefaultWithOptions<T>(
    defaultOption$: Observable<T | null | undefined>,
    options$: Observable<T[]>,
    comparator: (a: T, b: T) => boolean
): Observable<T[]> {
    return combineLatest([defaultOption$, options$]).pipe(
        map(([defaultOption, options]) =>
            defaultOption && !options.find(option => comparator(defaultOption, option))
                ? [defaultOption, ...options]
                : options
        )
    );
}
